// src/services/teamTasks.service.js
import { api } from './api';
import taskSyncService from './taskSync.service';

class TeamTasksService {
    // ============================================================
    // TEAM TASKS CRUD
    // ============================================================

    /**
     * Get all team tasks with filters
     */
    async getTeamTasks(filters = {}) {
        try {
            const params = new URLSearchParams();

            if (filters.status) params.append('status', filters.status);
            if (filters.priority) params.append('priority', filters.priority);
            if (filters.assigned_type) params.append('assigned_type', filters.assigned_type);
            if (filters.assigned_to) params.append('assigned_to', filters.assigned_to);
            if (filters.category) params.append('category', filters.category);
            if (filters.view_type) params.append('view_type', filters.view_type);

            console.log('🔍 Fetching team tasks with params:', params.toString());
            const response = await api.get(`/api/team-tasks?${params.toString()}`);
            console.log('✅ Team tasks fetched successfully:', response.data);

            // Debug: Log the structure of the first task to understand the data format
            if (response.data && response.data.data && response.data.data.length > 0) {
                console.log('🔍 Sample task structure:', JSON.stringify(response.data.data[0], null, 2));
                console.log('🔍 Assigned employee IDs type:', typeof response.data.data[0].assigned_employee_ids);
                console.log('🔍 Assigned employee IDs value:', response.data.data[0].assigned_employee_ids);
            }

            return response.data;
        } catch (error) {
            console.error('❌ Error fetching team tasks:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                statusText: error.response?.statusText
            });
            throw error;
        }
    }

    /**
     * Get single team task with full details
     */
    async getTeamTask(taskId) {
        try {
            const response = await api.get(`/api/team-tasks/${taskId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching team task:', error);
            throw error;
        }
    }

    /**
     * Create new team task
     */
    async createTeamTask(taskData) {
        try {
            console.log('🚀 Creating team task with data:', taskData);

            console.log('📝 Sending to team tasks endpoint:', taskData);
            const response = await api.post('/api/team-tasks', taskData);
            console.log('✅ Team task created successfully:', response.data);

            // Debug: Log the created task structure to understand what's returned
            if (response.data && response.data.data) {
                console.log('🔍 Created task structure:', JSON.stringify(response.data.data, null, 2));
                console.log('🔍 Created task assigned_employee_ids:', response.data.data.assigned_employee_ids);
                console.log('🔍 Created task assigned employees count:', response.data.data.assigned_employees_count);
            }

            return response.data;
        } catch (error) {
            console.error('❌ Error creating team task:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                statusText: error.response?.statusText
            });
            throw error;
        }
    }

    /**
     * Update team task
     */
    async updateTeamTask(taskId, taskData) {
        try {
            const response = await api.put(`/api/team-tasks/${taskId}`, taskData);

            // After successful update, sync with personal tasks
            if (response.success) {
                console.log(`🔄 Triggering personal task sync after team task update`);
                taskSyncService.syncPersonalTasksFromTeamTask(taskId).catch(syncError => {
                    console.error('Error syncing personal tasks after team task update:', syncError);
                });
            }

            return response.data;
        } catch (error) {
            console.error('Error updating team task:', error);
            throw error;
        }
    }

    /**
     * Clone/duplicate team task
     */
    async cloneTeamTask(taskId, titleSuffix = ' (Copy)') {
        try {
            // Clone by creating a new task with similar data
            const originalTask = await this.getTeamTask(taskId);
            if (originalTask.success && originalTask.data) {
                const task = originalTask.data;
                const clonedData = {
                    title: task.title + titleSuffix,
                    description: task.description,
                    assigned_type: task.assigned_type || task.assign_to_type,
                    assigned_employee_ids: task.assigned_employee_ids,
                    priority: task.priority,
                    deadline: task.deadline,
                    due_date: task.due_date,
                    estimated_hours: task.estimated_hours,
                    category: task.category,
                    tags: task.tags
                };

                return await this.createTeamTask(clonedData);
            }
            throw new Error('Original task not found');
        } catch (error) {
            console.error('Error cloning team task:', error);
            throw error;
        }
    }

    /**
     * Delete team task
     */
    async deleteTeamTask(taskId) {
        try {
            const response = await api.delete(`/api/team-tasks/${taskId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting team task:', error);
            throw error;
        }
    }

    // ============================================================
    // SUB-TASKS
    // ============================================================

    /**
     * Add sub-task to team task
     */
    async addSubTask(taskId, subTaskData) {
        try {
            const response = await api.post(`/api/team-tasks/${taskId}/subtasks`, subTaskData);
            return response.data;
        } catch (error) {
            console.error('Error adding sub-task:', error);
            throw error;
        }
    }

    /**
     * Update sub-task
     */
    async updateSubTask(taskId, subTaskId, subTaskData) {
        try {
            const response = await api.put(`/api/team-tasks/${taskId}/subtasks/${subTaskId}`, subTaskData);
            return response.data;
        } catch (error) {
            console.error('Error updating sub-task:', error);
            throw error;
        }
    }

    /**
     * Delete sub-task
     */
    async deleteSubTask(taskId, subTaskId) {
        try {
            const response = await api.delete(`/api/team-tasks/${taskId}/subtasks/${subTaskId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting sub-task:', error);
            throw error;
        }
    }

    // ============================================================
    // COMMENTS
    // ============================================================

    /**
     * Add comment to task
     */
    async addComment(taskId, commentData) {
        try {
            const response = await api.post(`/api/team-tasks/${taskId}/comments`, commentData);
            return response.data;
        } catch (error) {
            console.error('Error adding comment:', error);
            throw error;
        }
    }

    // ============================================================
    // ATTACHMENTS
    // ============================================================

    /**
     * Add attachment to task
     */
    async addAttachment(taskId, attachmentData) {
        try {
            const response = await api.post(`/api/team-tasks/${taskId}/attachments`, attachmentData);
            return response.data;
        } catch (error) {
            console.error('Error adding attachment:', error);
            throw error;
        }
    }

    /**
     * Upload file (placeholder - you'll need to implement actual file upload)
     */
    async uploadFile(file) {
        try {
            const formData = new FormData();
            formData.append('file', file);

            // Fixed: Use correct upload endpoint
            const response = await api.post('/api/upload/single', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error uploading file:', error);
            throw error;
        }
    }

    // ============================================================
    // WATCHERS
    // ============================================================

    /**
     * Add watcher to task
     */
    async addWatcher(taskId, watcherId) {
        try {
            const response = await api.post(`/api/team-tasks/${taskId}/watchers`, { watcher_id: watcherId });
            return response.data;
        } catch (error) {
            console.error('Error adding watcher:', error);
            throw error;
        }
    }

    /**
     * Remove watcher from task
     */
    async removeWatcher(taskId, watcherId) {
        try {
            const response = await api.delete(`/api/team-tasks/${taskId}/watchers/${watcherId}`);
            return response.data;
        } catch (error) {
            console.error('Error removing watcher:', error);
            throw error;
        }
    }

    // ============================================================
    // ANALYTICS
    // ============================================================

    /**
     * Get team task analytics
     */
    async getTeamAnalytics() {
        try {
            const response = await api.get('/api/team-tasks-analytics');
            return response.data;
        } catch (error) {
            console.error('Error fetching team analytics:', error);
            throw error;
        }
    }

    // ============================================================
    // UTILITY METHODS
    // ============================================================

    /**
     * Get task status color
     */
    getStatusColor(status) {
        const colors = {
            'pending': 'gray',
            'in_progress': 'blue',
            'under_review': 'yellow',
            'completed': 'green',
            'on_hold': 'orange'
        };
        return colors[status] || 'gray';
    }

    /**
     * Get priority color
     */
    getPriorityColor(priority) {
        const colors = {
            'immediate': 'red',
            'urgent': 'orange',
            'next_in_line': 'yellow',
            'scheduled': 'green',
            'deferred': 'gray'
        };
        return colors[priority] || 'gray';
    }

    /**
     * Format task for Kanban board
     */
    formatTasksForKanban(tasks) {
        const columns = {
            'pending': [],
            'in_progress': [],
            'under_review': [],
            'completed': [],
            'on_hold': []
        };

        tasks.forEach(task => {
            if (columns[task.status]) {
                columns[task.status].push(task);
            }
        });

        return columns;
    }

    /**
     * Calculate task progress based on subtasks completion
     */
    calculateProgress(task) {
        // If task is completed, return 100%
        if (task.status === 'completed') return 100;

        // Calculate subtask counts from the subtasks array if available
        let subtasksTotal = task.subtasks_total || 0;
        let subtasksCompleted = task.subtasks_completed || 0;

        // If we have detailed subtasks data, recalculate counts to ensure accuracy
        if (task.subtasks && Array.isArray(task.subtasks)) {
            subtasksTotal = task.subtasks.length;
            subtasksCompleted = task.subtasks.filter(st => st.status === 'completed').length;

            // Debug logging for troubleshooting
            if (subtasksTotal > 0) {
                console.log(`📊 Task ${task.id} subtask calculation:`, {
                    title: task.title,
                    backendTotal: task.subtasks_total,
                    backendCompleted: task.subtasks_completed,
                    calculatedTotal: subtasksTotal,
                    calculatedCompleted: subtasksCompleted,
                    subtaskStatuses: task.subtasks.map(st => ({ id: st.id, title: st.title, status: st.status }))
                });
            }
        }

        // If we have subtasks data, calculate based on subtasks completion
        if (subtasksTotal > 0) {
            return Math.round((subtasksCompleted / subtasksTotal) * 100);
        }

        // If no subtasks, use explicit completion percentage if available
        if (task.completion_percentage !== undefined && task.completion_percentage !== null) {
            return task.completion_percentage;
        }

        // Fallback: Calculate based on status
        const statusProgress = {
            'pending': 0,
            'in_progress': 50,
            'under_review': 75,
            'completed': 100,
            'on_hold': 25
        };

        return statusProgress[task.status] || 0;
    }

    /**
     * Get accurate subtask counts for a task
     */
    getSubtaskCounts(task) {
        let subtasksTotal = task.subtasks_total || 0;
        let subtasksCompleted = task.subtasks_completed || 0;

        // If we have detailed subtasks data, recalculate counts to ensure accuracy
        if (task.subtasks && Array.isArray(task.subtasks)) {
            subtasksTotal = task.subtasks.length;
            subtasksCompleted = task.subtasks.filter(st => st.status === 'completed').length;

            // Debug logging for troubleshooting
            if (subtasksTotal > 0 && (subtasksCompleted !== task.subtasks_completed || subtasksTotal !== task.subtasks_total)) {
                console.log(`🔧 Fixing subtask counts for task ${task.id}:`, {
                    title: task.title,
                    oldTotal: task.subtasks_total,
                    oldCompleted: task.subtasks_completed,
                    newTotal: subtasksTotal,
                    newCompleted: subtasksCompleted
                });
            }
        }

        return { subtasksTotal, subtasksCompleted };
    }
}

export default new TeamTasksService();
